import { useMemo, useState } from 'react'
import { Alert, Button, Card, Flex, Segmented, Select, Space, Table, Tag, Tooltip, Typography, theme } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { todayInTenantZone } from '@/shared/lib/format'
import { EmptyState } from '@/shared/ui/EmptyState'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { PermissionNotice } from '@/shared/ui/PermissionNotice'
import { RowActions } from '@/shared/ui/table/RowActions'
import { RatingCompareTable } from './RatingCompareTable'
import { RatingModal } from './RatingModal'
import { RatingTrendChart } from './RatingTrendChart'
import { RatingVoidModal } from './RatingVoidModal'
import {
  OVERALL_ROW,
  TREND_MAX_QUARTERS,
  fetchPersonRatings,
  fetchRatingCriteria,
  fetchRatingTrend,
  quarterOfDay,
  quartersEndingAt,
  type Rating,
} from './ratingApi'
import { useQuarterLabel } from './useQuarterLabel'

interface Props {
  personId: string
  hiredAt: string
  terminatedAt: string | null
}

type View = 'compare' | 'chart'

const PAGE_SIZE = 10
const RANGE_OPTIONS = [4, 8, TREND_MAX_QUARTERS] as const

export function RatingCard({ personId, hiredAt, terminatedAt }: Props) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { user } = useAuth()
  const { can } = usePermissions()
  const quarterLabel = useQuarterLabel()
  const canView = can('hr.rating.view')
  const canWrite = can('hr.rating.write')
  const canManageCriteria = can('hr.ratingcriterion.write')

  const [page, setPage] = useState(1)
  const [quartersBack, setQuartersBack] = useState<number>(RANGE_OPTIONS[1])
  const [view, setView] = useState<View>('compare')
  const [series, setSeries] = useState<string>(OVERALL_ROW)
  const [rateOpen, setRateOpen] = useState(false)
  const [voiding, setVoiding] = useState<Rating | null>(null)

  const quarters = useMemo(
    () => quartersEndingAt(quarterOfDay(todayInTenantZone()), quartersBack),
    [quartersBack],
  )
  const from = quarters[0] ?? ''
  const to = quarters[quarters.length - 1] ?? ''

  const history = useQuery({
    queryKey: queryKeys.hr.ratings(personId, page),
    queryFn: () => fetchPersonRatings(personId, page, PAGE_SIZE),
    enabled: canView && personId !== '',
  })

  const trend = useQuery({
    queryKey: queryKeys.hr.ratingTrend(personId, from, to),
    queryFn: () => fetchRatingTrend(personId, from, to),
    enabled: canView && personId !== '',
  })

  const criteria = useQuery({
    queryKey: queryKeys.hr.ratingCriteria(true),
    queryFn: () => fetchRatingCriteria(true),
    enabled: canView && canWrite,
    staleTime: 5 * 60_000,
  })

  if (!canView) {
    return (
      <Card title={t('hr.rating.title')}>
        <PermissionNotice
          permission="hr.rating.view"
          title={t('hr.rating.hiddenTitle')}
          reason={t('hr.rating.hiddenReason')}
        />
      </Card>
    )
  }

  const trendData = trend.data
  const selectedPoints =
    series === OVERALL_ROW
      ? trendData?.overall ?? []
      : trendData?.criteria.find((c) => c.criterionId === series)?.points ?? []

  const canVoid = (row: Rating) => !row.voided && (row.ratedBy === user?.id || canManageCriteria)

  const columns: ColumnsType<Rating> = [
    {
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_value, _row, index) => (
        <Typography.Text type="secondary">{(page - 1) * PAGE_SIZE + index + 1}</Typography.Text>
      ),
    },
    {
      key: 'quarter',
      title: t('hr.rating.quarter'),
      width: 140,
      render: (_value, row) => (
        <Typography.Text strong={!row.voided} delete={row.voided}>
          {quarterLabel(row.quarter)}
        </Typography.Text>
      ),
    },
    {
      key: 'jobTitle',
      title: t('hr.rating.jobTitle'),
      width: 160,
      render: (_value, row) => row.jobTitleName,
    },
    {
      key: 'orgNode',
      title: t('hr.rating.orgNode'),
      width: 160,
      render: (_value, row) => row.orgNodeName,
    },
    {
      key: 'average',
      title: t('hr.rating.average'),
      width: 100,
      align: 'right',
      render: (_value, row) => (
        <Typography.Text strong={!row.voided} type={row.voided ? 'secondary' : undefined}>
          {row.average === null ? t('common.dash') : row.average.toFixed(2)}
        </Typography.Text>
      ),
    },
    {
      key: 'scores',
      title: t('hr.rating.scores'),
      width: 240,
      render: (_value, row) => (
        <Space size={[4, 4]} wrap>
          {row.scores.map((score) => (
            <Tag key={score.criterionId} bordered={false}>
              {`${score.criterionName}: ${String(score.score)}`}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      key: 'ratedBy',
      title: t('hr.rating.ratedBy'),
      width: 150,
      render: (_value, row) => row.ratedByName,
    },
    {
      key: 'ratedOn',
      title: t('hr.rating.ratedOn'),
      width: 120,
      render: (_value, row) => row.ratedOn,
    },
    {
      key: 'comment',
      title: t('hr.rating.comment'),
      width: 220,
      render: (_value, row) =>
        row.comment === null ? t('common.dash') : <ExpandableHint text={row.comment} />,
    },
    {
      key: 'state',
      title: t('hr.rating.state'),
      width: 130,
      render: (_value, row) =>
        row.voided ? (
          <Tooltip
            title={t('hr.rating.voidedBy', {
              name: row.voidedByName ?? t('common.dash'),
              reason: row.voidReason ?? t('common.dash'),
            })}
          >
            <Tag>{t('hr.rating.stateVoided')}</Tag>
          </Tooltip>
        ) : (
          <Tag color="green">{t('hr.rating.stateValid')}</Tag>
        ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_value, row) => (
        <RowActions
          items={[
            {
              key: 'void',
              label: t('hr.rating.voidSubmit'),
              onClick: () => setVoiding(row),
              permission: 'hr.rating.write',
              hidden: !canVoid(row),
              danger: true,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <Card
      title={t('hr.rating.title')}
      extra={
        canWrite ? (
          <Button size="small" type="primary" onClick={() => setRateOpen(true)}>
            {t('hr.rating.rateAction')}
          </Button>
        ) : null
      }
    >
      <Flex wrap gap="small" align="center" justify="space-between" style={{ marginBottom: 12 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t('hr.rating.trendTitle')}
        </Typography.Title>
        <Space wrap>
          <Segmented<View>
            size="small"
            value={view}
            onChange={setView}
            aria-label={t('hr.rating.viewLabel')}
            options={[
              { value: 'compare', label: t('hr.rating.viewCompare') },
              { value: 'chart', label: t('hr.rating.viewChart') },
            ]}
          />
          {view === 'chart' ? (
            <Select<string>
              size="small"
              value={series}
              onChange={setSeries}
              aria-label={t('hr.rating.seriesLabel')}
              style={{ width: 200 }}
              options={[
                { value: OVERALL_ROW, label: t('hr.rating.seriesOverall') },
                ...(trendData?.criteria ?? []).map((c) => ({
                  value: c.criterionId,
                  label: c.active ? c.criterionName : `${c.criterionName} · ${t('hr.rating.inactive')}`,
                })),
              ]}
            />
          ) : null}
          <Select<number>
            size="small"
            value={quartersBack}
            onChange={(value) => {
              setQuartersBack(value)
              setSeries(OVERALL_ROW)
            }}
            aria-label={t('hr.rating.rangeLabel')}
            style={{ width: 140 }}
            options={RANGE_OPTIONS.map((count) => ({
              value: count,
              label: t('hr.rating.rangeQuarters', { count }),
            }))}
          />
        </Space>
      </Flex>

      {trend.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(trend.error, t)} />
      ) : null}

      {trendData && trendData.ratingCount === 0 ? (
        <EmptyState title={t('hr.rating.trendEmpty')} description={t('hr.rating.trendEmptyHint')} />
      ) : (
        <>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            {trendData === undefined || trendData.average === null
              ? t('common.dash')
              : t('hr.rating.periodAverage', {
                  average: trendData.average.toFixed(2),
                  count: trendData.ratingCount,
                })}
          </Typography.Paragraph>
          {trendData === undefined ? null : view === 'compare' ? (
            <RatingCompareTable trend={trendData} quarters={quarters} />
          ) : (
            <RatingTrendChart
              quarters={quarters}
              points={selectedPoints}
              color={token.colorPrimary}
              ariaLabel={t('hr.rating.trendAria')}
              quarterLabel={quarterLabel}
              countLabel={(count) => t('hr.rating.ratingCount', { count })}
            />
          )}
        </>
      )}

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        {t('hr.rating.historyTitle')}
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {t('hr.rating.snapshotNote')}
      </Typography.Paragraph>

      {history.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(history.error, t)} />
      ) : null}

      <Table<Rating>
        rowKey={(row) => row.id}
        columns={columns}
        dataSource={[...(history.data?.rows ?? [])]}
        loading={history.isPending}
        size="small"
        scroll={{ x: 1530 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: history.data?.total ?? 0,
          showSizeChanger: false,
          onChange: setPage,
        }}
        locale={{
          emptyText: (
            <EmptyState title={t('hr.rating.historyEmpty')} description={t('hr.rating.historyEmptyHint')} />
          ),
        }}
      />

      <RatingModal
        open={rateOpen}
        personId={personId}
        hiredAt={hiredAt}
        terminatedAt={terminatedAt}
        criteria={criteria.data ?? []}
        onClose={() => setRateOpen(false)}
      />

      {voiding === null ? null : <RatingVoidModal rating={voiding} onClose={() => setVoiding(null)} />}
    </Card>
  )
}
